/**
 * Karaktärsval — the three aesthetic choices that make a design *someone's*:
 * interior wood species, interior panel direction, and exterior treatment.
 *
 * These are DRAFT state (they live in `useBuilderStore` and travel through
 * `BuildInput`), never derived. Everything downstream — 3D materials, BOM row
 * names, price rules, cut lengths, build guide, timeline — reads them from
 * here so there is exactly one owner per choice.
 */

/**
 * Default interior wood for panels and benches. Authored bench modules may override it.
 */
export type InteriorWoodSpecies = "asp" | "al" | "gran";

/**
 * Which way the interior panel boards run.
 *
 * NOT cosmetic below the surface: the glespanel a board is nailed into must
 * cross it (korsläkt), so standing panel needs LYING battens and lying panel
 * needs STANDING ones. The batten quantity, its geometry and the panel's cut
 * lengths all follow from this field.
 */
export type InteriorPanelDirection = "standing" | "lying";

/**
 * Exterior treatment. `untreated` is the default and means "no facade colour
 * chosen" — the generic `systems.surface-treatment` allowance the BOM has
 * always carried stays, and no colour-specific row, tool, guide step or
 * drying time is added. Every other value buys real litres.
 */
export type ExteriorTreatmentId =
	| "untreated"
	| "tjarvitriol"
	| "jarnvitriol"
	| "slamfarg-red"
	| "slamfarg-black";

export interface FinishConfig {
	interiorWood: InteriorWoodSpecies;
	panelDirection: InteriorPanelDirection;
	exteriorTreatment: ExteriorTreatmentId;
}

/**
 * Defaults reproduce today's *appearance* exactly: the interior textures on
 * disk are aspen, the panel stands, and the facade is bare spruce.
 *
 * Note that `asp` also corrects a long-standing disagreement — the viewer has
 * rendered aspen panelling while the BOM priced it with `prices.bastupanel`
 * ("Bastupanel (gran)"). Picking `gran` now gives that older, cheaper quote
 * *and* re-tints the 3D to match it.
 */
export const DEFAULT_FINISHES: FinishConfig = {
	interiorWood: "asp",
	panelDirection: "standing",
	exteriorTreatment: "untreated",
};

export const INTERIOR_WOOD_SPECIES: readonly InteriorWoodSpecies[] = [
	"asp",
	"al",
	"gran",
];
export const PANEL_DIRECTIONS: readonly InteriorPanelDirection[] = [
	"standing",
	"lying",
];
export const EXTERIOR_TREATMENTS: readonly ExteriorTreatmentId[] = [
	"untreated",
	"tjarvitriol",
	"jarnvitriol",
	"slamfarg-red",
	"slamfarg-black",
];

function pick<T extends string>(
	value: unknown,
	allowed: readonly T[],
	fallback: T,
): T {
	return allowed.includes(value as T) ? (value as T) : fallback;
}

/**
 * Forward-migration for anything that crosses a persistence boundary: a
 * Supabase row written before the column existed, a share link from an older
 * build, a hand-edited jsonb. Anything unrecognised falls back to the default
 * for that field ALONE, so one bad value can't wipe the other two choices.
 */
export function normalizeFinishes(value: unknown): FinishConfig {
	if (!value || typeof value !== "object") {
		return { ...DEFAULT_FINISHES };
	}
	const raw = value as Partial<Record<keyof FinishConfig, unknown>>;
	return {
		interiorWood: pick(
			raw.interiorWood,
			INTERIOR_WOOD_SPECIES,
			DEFAULT_FINISHES.interiorWood,
		),
		panelDirection: pick(
			raw.panelDirection,
			PANEL_DIRECTIONS,
			DEFAULT_FINISHES.panelDirection,
		),
		exteriorTreatment: pick(
			raw.exteriorTreatment,
			EXTERIOR_TREATMENTS,
			DEFAULT_FINISHES.exteriorTreatment,
		),
	};
}

/** True when `finishes` is (or is absent and therefore equals) the default. */
export function isDefaultFinishes(finishes: FinishConfig | null | undefined) {
	const resolved = finishes ?? DEFAULT_FINISHES;
	return (
		resolved.interiorWood === DEFAULT_FINISHES.interiorWood &&
		resolved.panelDirection === DEFAULT_FINISHES.panelDirection &&
		resolved.exteriorTreatment === DEFAULT_FINISHES.exteriorTreatment
	);
}
